import React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { getLiveTrainingPortalRoot } from "../hooks/useLiveFullscreen";

function formatPlayerName(p) {
  if (!p) return "—";
  return (
    p.user_alias ||
    p.user_first_name ||
    p.user_last_name ||
    (p.player_id ? String(p.player_id).slice(-6) : "—")
  );
}

function WizardSteps({ step, t }) {
  const steps = [
    { id: "winner", label: t("kotWizardStepWinner") },
    { id: "scores", label: t("kotWizardStepScores") },
  ];
  const idx = steps.findIndex((s) => s.id === step);
  return (
    <div className="kot-wizard__steps" aria-label={t("kotWizardProgress")}>
      {steps.map((s, i) => (
        <React.Fragment key={s.id}>
          <div
            className={`kot-wizard__step-pill${
              i <= idx ? " kot-wizard__step-pill--active" : ""
            }${i === idx ? " kot-wizard__step-pill--current" : ""}`}
          >
            <span className="kot-wizard__step-num">{i + 1}</span>
            <span className="kot-wizard__step-label">{s.label}</span>
          </div>
          {i < steps.length - 1 ? (
            <span
              className={`kot-wizard__step-connector${
                i < idx ? " kot-wizard__step-connector--done" : ""
              }`}
              aria-hidden
            />
          ) : null}
        </React.Fragment>
      ))}
    </div>
  );
}

/**
 * 2-step coach KoT result wizard: pick winner → score + golden point + submit.
 */
export function KotCoachResultDialog({
  open,
  match,
  leaderboard = [],
  coach,
  formatPlayerLabel,
}) {
  const { t } = useTranslation();

  if (!open || !match || !coach) return null;

  const resolveName =
    formatPlayerLabel ||
    ((player, playerId) => {
      if (player) return formatPlayerName(player);
      if (playerId && Array.isArray(leaderboard)) {
        const row = leaderboard.find(
          (r) => String(r.player_id) === String(playerId)
        );
        if (row) return formatPlayerName(row);
      }
      if (playerId) return String(playerId).slice(-6);
      return "—";
    });

  const nameA = resolveName(match.player_a, match.player_a_id);
  const nameB = resolveName(match.player_b, match.player_b_id);
  const {
    wizardStep,
    winnerSide,
    loserScore,
    resolvedScores,
    goldenPointEnabled,
    submitting,
    error,
    pickWinner,
    adjustLoserScore,
    setGoldenPointEnabled,
    wizardBack,
    canSubmit,
    close,
    submit,
  } = coach;

  const { scoreA, scoreB } = resolvedScores;
  const winnerIsA = winnerSide === "A";
  const winnerIsB = winnerSide === "B";
  const winnerName = winnerIsA ? nameA : winnerIsB ? nameB : null;

  const errorText =
    error && (error.startsWith("kot") ? t(error) || error : error);

  const sheet = (
    <div
      className="kot-result-dialog__backdrop"
      role="presentation"
      onClick={() => !submitting && close()}
    >
      <div
        className="kot-result-dialog__sheet kot-wizard"
        role="dialog"
        aria-modal="true"
        aria-labelledby="kot-wizard-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="kot-wizard__header">
          <div>
            <h3 id="kot-wizard-title" className="kot-wizard__title">
              {t("kotCoachEnterResult")}
            </h3>
            <p className="kot-wizard__subtitle">
              {t("table_with_number", {
                number: match.table_number ?? "—",
              })}
            </p>
          </div>
          <button
            type="button"
            className="kot-wizard__close"
            onClick={close}
            disabled={submitting}
            aria-label={t("cancel")}
          >
            <CloseIcon fontSize="small" />
          </button>
        </header>

        <WizardSteps step={wizardStep} t={t} />

        <div className="kot-wizard__body">
          {wizardStep === "winner" ? (
            <>
              <p className="kot-wizard__hint">{t("kotWizardPickWinner")}</p>
              <div className="kot-wizard__winner-row">
                <button
                  type="button"
                  className={`kot-wizard__winner-btn${
                    winnerIsA ? " kot-wizard__winner-btn--selected" : ""
                  }`}
                  onClick={() => pickWinner("A")}
                >
                  {winnerIsA ? (
                    <CheckCircleIcon className="kot-wizard__winner-check" />
                  ) : null}
                  <span>{nameA}</span>
                </button>
                <button
                  type="button"
                  className={`kot-wizard__winner-btn${
                    winnerIsB ? " kot-wizard__winner-btn--selected" : ""
                  }`}
                  onClick={() => pickWinner("B")}
                >
                  {winnerIsB ? (
                    <CheckCircleIcon className="kot-wizard__winner-check" />
                  ) : null}
                  <span>{nameB}</span>
                </button>
              </div>
            </>
          ) : null}

          {wizardStep === "scores" ? (
            <>
              {winnerName ? (
                <p className="kot-wizard__hint kot-wizard__hint--winner">
                  {t("kotWizardLoserScore", { winner: winnerName })}
                </p>
              ) : null}
              <div className="kot-wizard__loser-only">
                <div className="kot-wizard__stepper-card kot-wizard__stepper-card--center">
                  <span className="kot-wizard__stepper-label">
                    {t("kotWizardLoserScoreLabel")}
                  </span>
                  <div className="kot-wizard__stepper-row">
                    <button
                      type="button"
                      className="kot-wizard__stepper-btn"
                      onClick={() => adjustLoserScore(-1)}
                      aria-label={t("kotWizardLoserScoreLabel")}
                    >
                      <RemoveIcon fontSize="small" />
                    </button>
                    <span className="kot-wizard__stepper-value">{loserScore}</span>
                    <button
                      type="button"
                      className="kot-wizard__stepper-btn"
                      onClick={() => adjustLoserScore(1)}
                      aria-label={t("kotWizardLoserScoreLabel")}
                    >
                      <AddIcon fontSize="small" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="kot-wizard__score-summary" aria-live="polite">
                <span
                  className={
                    winnerIsA
                      ? "kot-wizard__summary-win"
                      : "kot-wizard__summary-lose"
                  }
                >
                  {nameA} {scoreA}
                </span>
                <span className="kot-wizard__summary-dash">–</span>
                <span
                  className={
                    winnerIsB
                      ? "kot-wizard__summary-win"
                      : "kot-wizard__summary-lose"
                  }
                >
                  {scoreB} {nameB}
                </span>
              </div>
              <label className="kot-wizard__golden-row">
                <span>{t("kotGoldenPointCheckbox")}</span>
                <input
                  type="checkbox"
                  checked={goldenPointEnabled}
                  onChange={(e) => setGoldenPointEnabled(e.target.checked)}
                />
              </label>
            </>
          ) : null}

          {errorText ? (
            <p className="kot-result-dialog__error" role="alert">
              {errorText}
            </p>
          ) : null}
        </div>

        <footer className="kot-wizard__footer">
          {wizardStep === "scores" ? (
            <button
              type="button"
              className="kot-result-dialog__btn kot-result-dialog__btn--ghost"
              disabled={submitting}
              onClick={wizardBack}
            >
              {t("back") || "Back"}
            </button>
          ) : (
            <button
              type="button"
              className="kot-result-dialog__btn kot-result-dialog__btn--ghost"
              disabled={submitting}
              onClick={close}
            >
              {t("cancel")}
            </button>
          )}
          {wizardStep === "scores" ? (
            <button
              type="button"
              className="kot-result-dialog__btn kot-result-dialog__btn--primary"
              disabled={submitting || !canSubmit}
              onClick={submit}
            >
              {submitting ? t("submitting") : t("submit")}
            </button>
          ) : winnerSide ? (
            <button
              type="button"
              className="kot-result-dialog__btn kot-result-dialog__btn--primary"
              disabled={submitting}
              onClick={() => pickWinner(winnerSide)}
            >
              {t("kotWizardNext") || "Continue"}
            </button>
          ) : (
            <span className="kot-wizard__footer-spacer" aria-hidden />
          )}
        </footer>
      </div>
    </div>
  );

  if (typeof document === "undefined") return sheet;
  // Live Training fullscreen mounts on `.lt-page`; body portals are invisible there.
  const portalRoot = getLiveTrainingPortalRoot() || document.body;
  return createPortal(sheet, portalRoot);
}

export function KotMatchSubmittedIcon() {
  return (
    <CheckCircleIcon
      className="kot-settings__match-submitted-icon"
      aria-hidden
    />
  );
}

export default KotCoachResultDialog;
